import React, { useEffect, useState } from 'react';
import { Divider, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { findIndex, isNaN, sumBy } from 'lodash';
import moment from 'moment';

import Amazon from '../../../../../../../assets/icons/amazon-link-logo.svg';
import DeleteIcon from '../../../../../../../assets/icons/delete-border-icon.svg';
import ProductImage from '../../../../../../../assets/icons/product-image.svg';
import Button from "../../../../../../../components/uiComponent/button/index";
import Input from "../../../../../../../components/uiComponent/inputs/input/index"
import Modal from "../../../../../../../components/uiComponent/modal/index"
import SelectInput from '../../../../../../../components/uiComponent/inputs/selectableInput';

import { DrawerStyleWrapper } from '../../style'
import { CreateSingleSkuBox, GetPackingItems, GetWarehouseShipments } from '../../../../../../../slices/post-shipment';
import productLabel from '../../../../../../../utils/print-labels/productLabel';
import box2DLabels from '../../../../../../../utils/print-labels/box2DLabels';
import { mix2DLablePdfSinglePage } from '../../../../../../../actions/utils/mix2DLablePdf';
import { buildPackNowLabelItems, resolveFnsku, formatBoxCode } from './packNowLabelHelpers';
import ReactSelect from '../../../../../../../components/uiComponent/select';

const unitOptions = [
  { value: 'lb', label: 'lb' },
  { value: 'kg', label: 'kg' },
];

const initialBoxDetails = { noOfBoxes: 0, inABox: 0, length: 5, width: 5, height: 5, weight: 5, unit: 'lb' };

const AddItemDrawer = ({
  setProductInfo,
  productInfo,
  itemsList,
  itemShipmentIndex,
  setItemShipmentIndex,
  itemShipments,
  setItemShipments,
  setItemIndex,
  productDetail,
  setProductDetail,
  packingGroupId,
  packingMethod,
  skuGroupMap,
  boxRequirements,
  onBoxCreated,
}) => {
  const { workOrderId } = useParams();
  const dispatch = useDispatch();
  const isPackNow = packingMethod === 'pack_now';

  const [boxesDetail, setBoxesDetail] = useState([{ ...initialBoxDetails }]);

  const { creatingSingleSkuBox } = useSelector((state) => state.postShipment || {});

  const addRow = () => {
    setBoxesDetail(boxesDetail => [...boxesDetail, { ...initialBoxDetails }]);
  };

  const [fnskuLabels, setFnskuLabels] = useState(0);

  const handlePrintFnskuLabels = () => {
    const {
      item_name,
      prep_instructions,
      expiration,
      fnsku,
      min_bundle_quantity: bundleQty
    } = productDetail || {};

    const labelsData = {
      fnsku,
      title: item_name || '',
      expiryDate: expiration ? moment(expiration).format('MM-DD-YYYY') : 'N/A',
      bundleQty,
      numberOfLabels: Number(fnskuLabels),
      username: "Opulent",
      prepInstruction: prep_instructions,
    };

    productLabel(labelsData);
  };

  const removeRow = (index) => {
    setBoxesDetail(boxesDetail => {
      const updatedBoxes = [...boxesDetail];
      updatedBoxes.splice(index, 1);
      return updatedBoxes;
    });
  };

  const handleGenerateMix2DBoxLabel = (shipmentDetails = [], boxesToPrint) => {
    const {
      amazon_shipment_confirmation_id: shipmentId,
      name: shipmentName,
      amazon_shipment_id,
      amazon_warehouse_id: FCID,
      destination_name,
      destination_city,
      destination_postal_code,
      destination_address_line_1,
      destination_state_or_province_code,
      source_city,
      source_country_code,
      source_postal_code,
      source_name,
      source_address_line_1,
    } = itemShipments[itemShipmentIndex] || {};
    const { fnsku } = productDetail || {};

    const ship = {
      warehouse_name: source_name,
      address_line1: source_address_line_1,
      city: source_city,
      state_or_province_code: source_postal_code,
      country_code: source_country_code,
      ship_to_name: destination_name,
      destination_fulfillment_center_id: FCID,
      ship_to_address1: destination_address_line_1,
      ship_to_city: destination_city,
      ship_to_state: destination_state_or_province_code,
      ship_to_postal_code: destination_postal_code
    };

    const shipment = shipmentDetails?.shipments?.find(
      s => s.amazon_shipment_id === amazon_shipment_id
    );

    const boxesList = shipment?.boxes_info?.reduce((acc, box, index) => {
      if (boxesToPrint.includes(box.id)) {
        acc.push({ ...box, index: index + 1 });
      }
      return acc;
    }, []) || [];

    mix2DLablePdfSinglePage({
      shipmentId,
      userName: 'Opulent',
      shipmentName,
      dateCreated: moment().format("DD/MM/YY"),
      outbound_shipment_id: amazon_shipment_id,
      boxesExpanded: boxesList,
      title: fnsku,
      is2D: true,
      ship
    });
  };

  const handleGeneratePackNowBoxLabel = ({ packingBoxes = [], packingItems = [], boxIds = [] }) => {
    const printableBoxIds = boxIds.map(String);
    const boxesExpanded = packingBoxes.reduce((acc, box, index) => {
      if (printableBoxIds.includes(String(box.id))) {
        acc.push({ ...box, index: index + 1 });
      }
      return acc;
    }, []);

    if (!boxesExpanded.length) return;

    boxesExpanded.forEach((box) => {
      const mappedItems = box.items?.length
        ? buildPackNowLabelItems({ boxItems: box.items, packingItems, fallbackItem: productDetail })
        : (() => {
          const fnsku = resolveFnsku(productDetail);
          return [{
            fnsku,
            fulfillmentNetworkSku: fnsku,
            quantity: Number(box.units || box.quantity || 0),
            units: Number(box.units || box.quantity || 0),
            expiryDate: productDetail?.expiration ? [productDetail.expiration] : []
          }];
        })();

      const enrichedItems = mappedItems.map(mi => ({
        ...mi,
        name: productDetail?.item_name || productDetail?.name || '',
        msku: productDetail?.msku || productDetail?.sku || '',
      }));

      box2DLabels({
        isPackNow: true,
        boxCode: formatBoxCode(box.box_code, box.id),
        boxNumber: box.index,
        items: enrichedItems,
        dimensions: { length: box.length, width: box.width, height: box.height, unit: box.unit || 'lb' },
        weight: box.weight,
        createdAt: box.created_at,
      });
    });
  };

  const getTotalQuantity = () => {
    return boxesDetail.reduce((sum, box) => {
      return sum + ((Number(box.noOfBoxes) || 0) * (Number(box.inABox) || 0));
    }, 0);
  };

  const handleSubmitBoxCreation = () => {
    const { sku_id } = productDetail || {};

    if (isPackNow) {
      const resolvedGroupId = (skuGroupMap && productDetail?.msku && skuGroupMap[productDetail.msku]) || packingGroupId;
      dispatch(CreateSingleSkuBox({
        sku_id,
        boxesDetail,
        packingGroupId: resolvedGroupId,
        workOrderId: Number(workOrderId),
      })).then(({ payload }) => {
        if (payload?.success) {
          const boxIds = payload?.boxIds || (payload?.boxId ? [payload.boxId] : []);
          setBoxesDetail([{ ...initialBoxDetails }]);
          setProductInfo(false);
          dispatch(GetPackingItems({ workOrderId })).then(({ payload: packingPayload }) => {
            handleGeneratePackNowBoxLabel({
              packingBoxes: packingPayload?.packingBoxes || [],
              packingItems: packingPayload?.packingItems || [],
              boxIds
            });
            onBoxCreated?.();
          });
        }
      });
      return;
    }

    // Pack Later path
    let boxIds = [];
    dispatch(CreateSingleSkuBox({
      sku_id,
      boxesDetail,
      amazonShipmentId: itemShipments[itemShipmentIndex]?.amazon_shipment_id,
      shipmentItemId: itemShipments[itemShipmentIndex]?.shipment_item_id || productDetail?.id,
      workOrderId: Number(workOrderId)
    })).then(({ payload }) => {
      if (payload?.success) {
        boxIds = payload?.boxIds || [];
        const hasNonZeroRemainingQuantity = itemShipments
          .filter(shipment => Number(shipment.boxed_quantity || 0) !== Number(shipment.quantity || 0))
          .some((shipment) => {
            const remainingQuantity = Number(shipment?.quantity) - ((shipment.boxed_quantity || 0) + getTotalQuantity());
            return remainingQuantity !== 0;
          });

        if (!hasNonZeroRemainingQuantity) setProductInfo(false);
        else {
          setBoxesDetail([{ ...initialBoxDetails }]);
          setProductDetail((prev) => ({
            boxed_quantity: Number(prev?.boxed_quantity || 0) + getTotalQuantity(),
            ...prev
          }));
          setItemShipments(itemShipments => {
            const updatedShipments = [...itemShipments];
            updatedShipments[itemShipmentIndex] = {
              ...updatedShipments[itemShipmentIndex],
              boxed_quantity: Number(updatedShipments[itemShipmentIndex].boxed_quantity) + getTotalQuantity()
            };
            return updatedShipments;
          });
        }

        dispatch(GetWarehouseShipments({ workOrderId })).then(({ payload }) => {
          handleGenerateMix2DBoxLabel(payload?.shipmentDetails, boxIds);
        });
      }
    });
  };

  const getCreateBtnDisabledStatus = () => {
    if (creatingSingleSkuBox) return true;
    const currentConfigQty = getTotalQuantity();
    const hasInvalidQty = boxesDetail.some(box =>
      !box.noOfBoxes || !box.inABox ||
      isNaN(Number(box.noOfBoxes)) || isNaN(Number(box.inABox)) ||
      (Number(box.noOfBoxes) <= 0) || (Number(box.inABox) <= 0)
    );

    const remainingQty = isPackNow
      ? (totalQty - boxedQty)
      : (Number(itemShipments[itemShipmentIndex]?.quantity) - (itemShipments[itemShipmentIndex]?.boxed_quantity || 0));

    const isQtyExceeded = currentConfigQty > remainingQty;

    if (isPackNow) {
      const hasInvalidDims = boxesDetail.some(box =>
        !box.length || !box.width || !box.height || !box.weight ||
        isNaN(Number(box.length)) || isNaN(Number(box.width)) || isNaN(Number(box.height)) || isNaN(Number(box.weight)) ||
        Number(box.length) <= 0 || Number(box.width) <= 0 || Number(box.height) <= 0 || Number(box.weight) <= 0
      );

      const hasWeightExceeded = boxesDetail.some(box =>
        (box.unit === 'kg' && Number(box.weight) > 22.6) ||
        ((box.unit || 'lb') === 'lb' && Number(box.weight) > 50)
      );

      const resolvedGroupId = (skuGroupMap && productDetail?.msku && skuGroupMap[productDetail.msku]) || packingGroupId;
      return !boxesDetail?.length || hasInvalidQty || hasInvalidDims || hasWeightExceeded || !resolvedGroupId || isQtyExceeded;
    }

    return (
      (itemShipmentIndex === -1) || !boxesDetail?.length || isQtyExceeded || hasInvalidQty
    );
  };

  const handleInputChange = (index, field, value) => {
    setBoxesDetail(boxesDetail => {
      const updatedBoxes = [...boxesDetail];
      updatedBoxes[index] = { ...updatedBoxes[index], [field]: value };
      return updatedBoxes;
    });
  };


  const handleCancel = () => {
    setProductInfo(false);
    setProductDetail({});
  };

  useEffect(() => {
    if (isPackNow || !itemShipments?.length) return;
    const currentShipment = itemShipments[itemShipmentIndex];
    const currentHasRemainingQty =
      Number(currentShipment?.quantity || 0) > Number(currentShipment?.boxed_quantity || 0);

    if (!currentHasRemainingQty) {
      const firstAvailableIndex = itemShipments.findIndex(
        shipment => Number(shipment.quantity || 0) > Number(shipment.boxed_quantity || 0)
      );

      if (firstAvailableIndex !== -1) {
        setItemShipmentIndex(firstAvailableIndex);
        setBoxesDetail([{ ...initialBoxDetails }]);

        const shipmentId = itemShipments[firstAvailableIndex]?.shipment_item_id;
        const matchedItemIndex = findIndex(itemsList, ({ id }) => id === shipmentId);
        setItemIndex(matchedItemIndex);
      }
    }
  }, [itemShipments]);

  const totalQty = sumBy(
    itemsList.filter(({ msku = '' }) => msku === productDetail?.msku),
    (item) => Number(item.quantity)
  );
  const boxedQty = sumBy(
    itemsList.filter(({ msku = '' }) => msku === productDetail?.msku),
    (item) => Number(item.boxed_quantity || 0)
  );

  return (
    <Modal
      removeIcon={true}
      width={1012}
      closeable={true}
      onClose={() => { setProductInfo(false); setProductDetail({}); }}
      open={productInfo}
      className="product-info-modal-wrapper"
      centered={true}
    >
      <DrawerStyleWrapper>
        <header />
        <div className='product-info-ui-wrapper'>
          <img src={productDetail?.image_url || ProductImage} alt="product-image" />
          <div className='content-drawer-product-info'>
            <div className='product-info-wrapper'>
              <div className='title'>
                <p>Title</p>
                <div className='title-item'>
                  {(() => {
                    const productAsin = productDetail?.asin || productDetail?.vendorasin?.asin || (productDetail?.product_details || []).find(p => p.identifier_type?.toUpperCase() === 'ASIN')?.identifier_value;
                    return (
                      <>
                        <p
                          style={{ cursor: 'pointer' }}
                          onClick={() => window.open(`https://www.amazon.com/gp/product/${productAsin}`, "_blank")}
                        >
                          {productDetail?.item_name}
                        </p>
                        <img src={Amazon} alt="Amazon"
                          style={{ cursor: 'pointer' }}
                          onClick={() => window.open(`https://www.amazon.com/gp/product/${productAsin}`, "_blank")}
                        />
                      </>
                    );
                  })()}
                </div>
                <div className='units-wrapper'>
                  <div className='units-items'>
                    <p>Total Qty</p>
                    <p>{totalQty}</p>
                  </div>
                  <div className='units-items'>
                    <p>Packed Qty</p>
                    <p>{boxedQty}</p>
                  </div>
                  <div className='units-items2 instruction-items2'>
                    <p style={{ fontWeight: 'bold', color: 'red' }}>Bundle Qty</p>
                    <p style={{ color: 'red' }}>{productDetail?.min_bundle_quantity || '1'}</p>
                  </div>
                  <div className='units-items'>
                    <p>Remaining Qty  </p>
                    <p>{totalQty - boxedQty}</p>
                  </div>
                  <div className='units-items instruction-items'>
                    <p>Prep instructions</p>
                    <p>{productDetail?.prep_instructions || 'N/A'}</p>
                  </div>
                </div>
                <Divider />
              </div>
            </div>
            <div className='box-info-overlay-wrapper'>
              <div className='title'>
                <div className='title-item'>
                  <p>Print FNSKU Labels</p>
                </div>
              </div>
              <div className={'fnsku-label-wrapper '}>
                <Input
                  placeholder='Enter Qty'
                  overlayClassName='enter-qty-field'
                  value={fnskuLabels || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) setFnskuLabels(value);
                  }}
                />
                <div className='buttons-wrapper' style={{ marginLeft: '17px', display: 'flex', alignContent: 'center', justifyContent: 'center' }}>
                  <Button
                    onClick={handlePrintFnskuLabels}
                    text="Print"
                    disabled={!fnskuLabels || Number(fnskuLabels) <= 0 || !Number(fnskuLabels)}
                  />
                </div>
              </div>

              {/* Shipment selection — Pack Later only */}
              {!isPackNow && (
                <div className='select-shipment-wrapper'>
                  <p className='title-wrap'>Select Shipment</p>
                  <div className="shipment-cards">
                    {itemShipments.map((shipment, index) => (
                      <div
                        key={shipment.id}
                        style={Number((shipment.quantity || 0) - (shipment.boxed_quantity || 0)) ? {} : { display: 'none' }}
                        className={`shipment-card ${itemShipmentIndex === index ? "active" : ""}`}
                        onClick={() => {
                          setItemShipmentIndex(index);
                          setBoxesDetail([{ ...initialBoxDetails }]);
                          const itemIndex = findIndex(itemsList, ({ id }) => id === itemShipments[index]?.shipment_item_id);
                          const item = itemsList.find(({ id }) => id === itemShipments[index]?.shipment_item_id);
                          if (item) setProductDetail(item);
                          setItemIndex(itemIndex);
                        }}
                      >
                        <div className="items">
                          <p>Shipment ID:</p>
                          <p>{shipment.amazon_shipment_confirmation_id}</p>
                        </div>
                        <div className="items">
                          <p>Total Qty:</p>
                          <p>{shipment.quantity}</p>
                        </div>
                        <div className="items">
                          <p>Remaining Qty:</p>
                          <p>{shipment.quantity - (shipment.boxed_quantity || 0)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className='title' style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isPackNow ? '24px' : '16px' }}>
                <div className='title-item'>
                  <p style={!isPackNow ? { color: '#0061E1', fontSize: '20px', fontWeight: '700', margin: 0 } : {}}>Box Information</p>
                </div>
                <Button
                  onClick={addRow}
                  text="Add New Box Config"
                  disabled={creatingSingleSkuBox || (!isPackNow && itemShipmentIndex === -1)}
                />
              </div>
              <div className='input-boxes-overlay-wrapper' style={{ marginTop: isPackNow ? '24px' : '0' }}>
                {isPackNow && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    marginBottom: '8px',
                    color: '#7A8699',
                    fontSize: '11px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    padding: '0 4px'
                  }}>
                    <div style={{ flex: 1, textAlign: 'center' }}>Boxes</div>
                    <div style={{ flex: 1, textAlign: 'center' }}>In Box</div>
                    <div style={{ flex: 1, textAlign: 'center' }}>Total</div>
                    <div style={{ flex: 1, textAlign: 'center' }}>L</div>
                    <div style={{ flex: 1, textAlign: 'center' }}>W</div>
                    <div style={{ flex: 1, textAlign: 'center' }}>H</div>
                    <div style={{ flex: 2, textAlign: 'center' }}>Weight</div>
                    <div style={{ width: '36px', flexShrink: 0 }} />
                  </div>
                )}

                {boxesDetail?.map((box, index) => (
                  <div key={index} style={!isPackNow ? { marginBottom: '16px' } : {}}>
                    {!isPackNow ? (
                      <div style={{ display: 'flex', gap: '16px', padding: '0 4px' }}>
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: '0 0 6px 0', fontSize: '13px', fontWeight: '600', color: '#344054' }}>No. of Boxes</p>
                          <Input placeholder='' label={null} height="40px"
                            value={box.noOfBoxes || ''}
                            disabled={!isPackNow && itemShipmentIndex === -1}
                            onChange={(e) => { if (/^\d*$/.test(e.target.value)) handleInputChange(index, 'noOfBoxes', e.target.value); }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: '0 0 6px 0', fontSize: '13px', fontWeight: '600', color: '#344054' }}>Qty in Box</p>
                          <Input placeholder='' label={null} height="40px"
                            value={box.inABox || ''}
                            disabled={!isPackNow && itemShipmentIndex === -1}
                            onChange={(e) => { if (/^\d*$/.test(e.target.value)) handleInputChange(index, 'inABox', e.target.value); }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: '0 0 6px 0', fontSize: '13px', fontWeight: '600', color: '#344054' }}>Total Qty</p>
                          <Input placeholder='' label={null} height="40px"
                            value={(box.noOfBoxes || 0) * (box.inABox || 0)} disabled={true}
                            style={{ backgroundColor: '#E9EDF1', border: '1px solid #D0D5DD' }}
                          />
                        </div>
                        <div style={{ width: '50px', display: 'flex', flexDirection: 'column' }}>
                          <p style={{ margin: '0 0 6px 0', fontSize: '13px', visibility: 'hidden' }}>Spacer</p>
                          <div style={{ height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {index !== 0 && (
                              <Tooltip title='Delete'>
                                <img src={DeleteIcon} alt='DeleteIcon' onClick={() => removeRow(index)}
                                  style={{ cursor: 'pointer', width: '24px', opacity: 0.8 }}
                                />
                              </Tooltip>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 4px',
                        borderBottom: '1px solid #F0F2F5'
                      }}>
                        <div style={{ flex: 1 }}>
                          <Input placeholder='Boxes' label={null} height="36px"
                            value={box.noOfBoxes || ''}
                            disabled={!isPackNow && itemShipmentIndex === -1}
                            onChange={(e) => { if (/^\d*$/.test(e.target.value)) handleInputChange(index, 'noOfBoxes', e.target.value); }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <Input placeholder='In Box' label={null} height="36px"
                            value={box.inABox || ''}
                            disabled={!isPackNow && itemShipmentIndex === -1}
                            onChange={(e) => { if (/^\d*$/.test(e.target.value)) handleInputChange(index, 'inABox', e.target.value); }}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <Input placeholder='Total' label={null} height="36px"
                            value={(box.noOfBoxes || 0) * (box.inABox || 0)} disabled={true}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <Input placeholder='L' label={null} height="36px"
                            value={box.length || ''}
                            disabled={!isPackNow && itemShipmentIndex === -1}
                            onChange={(e) => handleInputChange(index, 'length', e.target.value)}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <Input placeholder='W' label={null} height="36px"
                            value={box.width || ''}
                            disabled={!isPackNow && itemShipmentIndex === -1}
                            onChange={(e) => handleInputChange(index, 'width', e.target.value)}
                          />
                        </div>
                        <div style={{ flex: 1 }}>
                          <Input placeholder='H' label={null} height="36px"
                            value={box.height || ''}
                            disabled={!isPackNow && itemShipmentIndex === -1}
                            onChange={(e) => handleInputChange(index, 'height', e.target.value)}
                          />
                        </div>
                        <div style={{ flex: 2, display: 'flex', alignItems: 'center' }}>
                          <div style={{ flex: 1 }}>
                            <Input placeholder='Wt' label={null} height="36px"
                              value={box.weight || ''}
                              disabled={!isPackNow && itemShipmentIndex === -1}
                              className={((box.unit || 'lb') === 'lb' && Number(box.weight) > 50) || (box.unit === 'kg' && Number(box.weight) > 22.6) ? 'danger-border' : ''}
                              onChange={(e) => handleInputChange(index, 'weight', e.target.value)}
                              style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0, borderRight: 'none' }}
                            />
                          </div>
                          <div style={{ width: '75px', marginLeft: '-1px' }}>
                            <ReactSelect
                              options={unitOptions} placeholder="lb" label={null}
                              value={box.unit ? { value: box.unit, label: box.unit } : { value: 'lb', label: 'lb' }}
                              disabled={!isPackNow && itemShipmentIndex === -1}
                              onChange={(e) => handleInputChange(index, 'unit', e?.value)}
                              menuPortalTarget={true} menuPosition="fixed"
                              styles={{
                                control: (base) => ({ ...base, height: '36px', minHeight: '36px', borderRadius: '0 4px 4px 0', borderColor: '#CFD8DC', fontSize: '11px', boxShadow: 'none', '&:hover': { borderColor: '#CFD8DC' } }),
                                valueContainer: (base) => ({ ...base, padding: '0 4px', height: '36px' }),
                                indicatorsContainer: (base) => ({ ...base, height: '36px' }),
                                singleValue: (base) => ({ ...base, margin: 0 })
                              }}
                            />
                          </div>
                        </div>
                        <div style={{ width: '36px', height: '36px', flexShrink: 0, alignSelf: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {index !== 0 && (
                            <Tooltip title='Delete'>
                              <img src={DeleteIcon} alt='DeleteIcon' onClick={() => removeRow(index)}
                                style={{ cursor: 'pointer', width: '24px', height: '24px' }}
                              />
                            </Tooltip>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className='label-print-wrapper'>
                {isPackNow && boxesDetail.some(box => (box.unit === 'kg' && Number(box.weight) > 22.6) || ((box.unit || 'lb') === 'lb' && Number(box.weight) > 50)) && (
                  <p style={{ color: '#D95040', fontSize: '12px', textAlign: 'right', marginBottom: '8px', width: '100%' }}>One or more boxes exceed weight limit (50lb / 22.6kg)</p>
                )}
                <div className='buttons-wrapper'>
                  <Button
                    onClick={handleCancel}
                    disabled={creatingSingleSkuBox}
                    text="Cancel"
                    className="outlined"
                  />
                  <Button
                    text={creatingSingleSkuBox ? 'Adding..' : "Print Box Label"}
                    disabled={getCreateBtnDisabledStatus()}
                    onClick={() => handleSubmitBoxCreation()}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </DrawerStyleWrapper>
    </Modal>
  );
};

export default AddItemDrawer;
